import { type TFunction } from "i18next";
import {
  ArrowLeftRight,
  BadgeDollarSign,
  BadgeQuestionMark,
  Banknote,
  BanknoteCheck,
  BanknoteX,
  Bean,
  BookOpenCheck,
  CalendarClock,
  CircleDollarSign,
  HandCoins,
  House,
  MailCheck,
  MailPlus,
  Mails,
  PackageCheck,
  Scale,
  SearchCheck,
  Shovel,
  UserPlus,
  Users,
  Warehouse,
  Wheat,
} from "lucide-react";

import { type MoneyByCurrency, type QuantityByUnit, type WorkflowData } from "../../types";
import { type WorkflowGroup, type WorkflowValue } from "./types";

const LABEL_PREFIX = "home.manager.overview.workflow";

const numberFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

/**
 * Tooltips promise the exact figure, so they must not round: DRF sends quantities
 * with 5 decimals, and 3-decimal currencies (KWD, BHD) exist.
 */
const exactFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 5 });

const compactFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/** Below this the full number reads fine, and "1K" would hide more than it saves. */
const COMPACT_THRESHOLD = 10_000;

/**
 * Every formatter returns the parts of a value rather than one joined string: the
 * card renders a single part inline with its label and stacks several beneath it,
 * so a multi-unit total cannot squeeze the label out of the row.
 */
const formatNumber = (value: number | undefined | null): string =>
  numberFormatter.format(typeof value === "number" && Number.isFinite(value) ? value : 0);

const formatCount = (value: number | undefined | null): WorkflowValue[] => [
  { display: formatNumber(value) },
];

/** Decimals arrive as strings from DRF, so parse before formatting. */
const parseDecimal = (value: string): number => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Money and unit totals reach billions, which no card column can hold in full. */
const formatCompact = (value: number): string =>
  Math.abs(value) < COMPACT_THRESHOLD
    ? numberFormatter.format(value)
    : compactFormatter.format(value);

/** Tooltip only when compacting actually hid something. */
const withTooltip = (display: string, exact: string, tooltip: string): WorkflowValue =>
  display === exact ? { display } : { display, tooltip };

/** ["3,250 kg", "4.2M kg", "480 bags"], or ["0"] when nothing was recorded. */
const formatQuantityByUnit = (
  quantities: QuantityByUnit[] | undefined,
  t: TFunction
): WorkflowValue[] => {
  if (!quantities?.length) return formatCount(0);
  return quantities.map(({ quantity, unit_label }) => {
    const parsed = parseDecimal(quantity);
    const exact = `${exactFormatter.format(parsed)} ${unit_label}`;
    return withTooltip(
      `${formatCompact(parsed)} ${unit_label}`,
      exact,
      t(`${LABEL_PREFIX}.exactValue`, { value: exact })
    );
  });
};

/** ["৳158.5B"], one entry per currency when a country records more than one. */
const formatMoney = (amounts: MoneyByCurrency[] | undefined, t: TFunction): WorkflowValue[] => {
  if (!amounts?.length) return formatCount(0);
  return amounts.map(({ amount, currency_symbol, currency_code }) => {
    const prefix = currency_symbol || `${currency_code} `;
    const parsed = parseDecimal(amount);
    const exact = `${prefix}${exactFormatter.format(parsed)}`;
    return withTooltip(
      `${prefix}${formatCompact(parsed)}`,
      exact,
      t(`${LABEL_PREFIX}.exactAmount`, { value: exact })
    );
  });
};

/**
 * Maps the `workflow` block onto the five task cards. Row order matches the
 * ticket's layout, which differs from the order keys arrive in the response.
 */
export const adaptWorkflowSummary = (data: WorkflowData, t: TFunction): WorkflowGroup[] => {
  const onboarding = data.farmer_onboarding;
  const traceability = data.production_traceability;
  const market = data.market_access;
  const payments = data.payments;
  const advisory = data.agro_advisory;

  // The gender fields were added after household_members, so older records don't add up.
  // Show the split only when it reconciles with the total.
  const householdSplitAddsUp =
    (onboarding?.household_members_male ?? 0) + (onboarding?.household_members_female ?? 0) ===
    onboarding?.household_members;

  return [
    {
      id: "farmer_onboarding",
      title: t(`${LABEL_PREFIX}.farmerOnboarding.title`),
      icon: Users,
      metrics: [
        {
          id: "farmer_registrations",
          label: t(`${LABEL_PREFIX}.farmerOnboarding.farmerRegistrations`),
          values: formatCount(onboarding?.farmer_registrations),
          icon: UserPlus,
          subMetrics: [
            {
              id: "farmer_registrations_male",
              label: t(`${LABEL_PREFIX}.farmerOnboarding.maleFarmers`),
              value: formatNumber(onboarding?.farmer_registrations_male),
            },
            {
              id: "farmer_registrations_female",
              label: t(`${LABEL_PREFIX}.farmerOnboarding.femaleFarmers`),
              value: formatNumber(onboarding?.farmer_registrations_female),
            },
          ],
        },
        {
          id: "household_members",
          label: t(`${LABEL_PREFIX}.farmerOnboarding.householdMembers`),
          values: formatCount(onboarding?.household_members),
          icon: House,
          subMetrics: householdSplitAddsUp
            ? [
                {
                  id: "household_members_male",
                  label: t(`${LABEL_PREFIX}.farmerOnboarding.maleMembers`),
                  value: formatNumber(onboarding.household_members_male),
                },
                {
                  id: "household_members_female",
                  label: t(`${LABEL_PREFIX}.farmerOnboarding.femaleMembers`),
                  value: formatNumber(onboarding.household_members_female),
                },
              ]
            : undefined,
        },
      ],
    },
    {
      id: "production_traceability",
      title: t(`${LABEL_PREFIX}.productionTraceability.title`),
      icon: SearchCheck,
      metrics: [
        {
          id: "commodity_events",
          label: t(`${LABEL_PREFIX}.productionTraceability.commodityEvents`),
          values: formatCount(traceability?.commodity_events),
          icon: CalendarClock,
        },
        {
          id: "harvests",
          label: t(`${LABEL_PREFIX}.productionTraceability.harvests`),
          values: formatCount(traceability?.harvests),
          icon: Wheat,
        },
        {
          id: "harvest_quantity",
          label: t(`${LABEL_PREFIX}.productionTraceability.harvestQuantity`),
          values: formatQuantityByUnit(traceability?.harvest_quantity, t),
          icon: Scale,
        },
        {
          id: "deposits",
          label: t(`${LABEL_PREFIX}.productionTraceability.deposits`),
          values: formatCount(traceability?.deposits),
          icon: Warehouse,
        },
        {
          id: "deposit_quantity",
          label: t(`${LABEL_PREFIX}.productionTraceability.depositQuantity`),
          values: formatQuantityByUnit(traceability?.deposit_quantity, t),
          icon: Scale,
        },
      ],
    },
    {
      id: "market_access",
      title: t(`${LABEL_PREFIX}.marketAccess.title`),
      icon: ArrowLeftRight,
      metrics: [
        {
          id: "direct_offers",
          label: t(`${LABEL_PREFIX}.marketAccess.directOffers`),
          values: formatCount(market?.direct_offers),
          icon: BadgeDollarSign,
        },
        {
          id: "demands",
          label: t(`${LABEL_PREFIX}.marketAccess.demands`),
          values: formatCount(market?.demands),
          icon: BadgeQuestionMark,
        },
        {
          id: "offers_accepted",
          label: t(`${LABEL_PREFIX}.marketAccess.offersAccepted`),
          values: formatCount(market?.offers_accepted),
          icon: BanknoteCheck,
        },
        {
          id: "offers_rejected",
          label: t(`${LABEL_PREFIX}.marketAccess.offersRejected`),
          values: formatCount(market?.offers_rejected),
          icon: BanknoteX,
        },
        {
          id: "deliveries_completed",
          label: t(`${LABEL_PREFIX}.marketAccess.deliveriesCompleted`),
          values: formatCount(market?.deliveries_completed),
          icon: PackageCheck,
        },
      ],
    },
    {
      id: "payments",
      title: t(`${LABEL_PREFIX}.payments.title`),
      icon: Banknote,
      metrics: [
        {
          id: "aggregator_payments_count",
          label: t(`${LABEL_PREFIX}.payments.aggregatorPaymentsReceived`),
          values: formatCount(payments?.aggregator_payments_count),
          icon: Warehouse,
        },
        {
          id: "aggregator_payment_amount",
          label: t(`${LABEL_PREFIX}.payments.aggregatorPaymentAmount`),
          values: formatMoney(payments?.aggregator_payment_amount, t),
          icon: CircleDollarSign,
        },
        {
          id: "farmer_payments_count",
          label: t(`${LABEL_PREFIX}.payments.farmerPaymentsReceived`),
          values: formatCount(payments?.farmer_payments_count),
          icon: HandCoins,
        },
        {
          id: "farmer_payment_amount",
          label: t(`${LABEL_PREFIX}.payments.farmerPaymentAmount`),
          values: formatMoney(payments?.farmer_payment_amount, t),
          icon: CircleDollarSign,
        },
      ],
    },
    {
      id: "agro_advisory",
      title: t(`${LABEL_PREFIX}.agroAdvisory.title`),
      icon: Mails,
      metrics: [
        {
          id: "messages_sent",
          label: t(`${LABEL_PREFIX}.agroAdvisory.messagesSent`),
          values: formatCount(advisory?.messages_sent),
          icon: MailPlus,
        },
        {
          id: "messages_read",
          label: t(`${LABEL_PREFIX}.agroAdvisory.messagesRead`),
          values: formatCount(advisory?.messages_read),
          icon: MailCheck,
        },
        {
          id: "farmers_trained",
          label: t(`${LABEL_PREFIX}.agroAdvisory.farmersTrained`),
          values: formatCount(advisory?.farmers_trained),
          icon: BookOpenCheck,
        },
        {
          id: "input_support_quantity",
          label: t(`${LABEL_PREFIX}.agroAdvisory.inputSupportQuantity`),
          values: formatQuantityByUnit(advisory?.input_support_quantity, t),
          icon: Bean,
        },
        {
          id: "equipment_support_quantity",
          label: t(`${LABEL_PREFIX}.agroAdvisory.equipmentSupportQuantity`),
          values: formatCount(advisory?.equipment_support_quantity),
          icon: Shovel,
        },
      ],
    },
  ];
};
